import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

const AuthContext = createContext(null);

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function explainAxiosError(e) {
  // Backend replied with JSON detail
  const fromDetail = formatApiErrorDetail(e.response?.data?.detail);
  if (fromDetail) return fromDetail;
  // Backend replied with plain status (no detail)
  if (e.response?.status) return `Server returned ${e.response.status} ${e.response.statusText || ""}`.trim();
  // Request sent but no response — network / CORS / timeout
  if (e.request) return "Cannot reach server. Check internet / VPN, clear cookies, or try an incognito window.";
  return e.message || "Unexpected error";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: explainAxiosError(e) };
    }
  };

  const register = async (email, password, full_name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, full_name });
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: explainAxiosError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {} 
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, api }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { api };
